import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { AdminLayout } from './layouts/AdminLayout'
import { AlumniLayout } from './layouts/AlumniLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { PublicLayout } from './layouts/PublicLayout'
import {
  AdminRoute,
  AlumniRoute,
  GlobalOperationsRoute,
  ProfileSettingsRoute,
  ProtectedRoute,
  StatusRoute,
  SuperadminRoute,
} from './RouteGuards'
import { PageLoading } from '../components/ui/States'
import ErrorPage from '../routes/ErrorPage'
const load = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(factory)
  return (
    <Suspense fallback={<PageLoading />}>
      <Component />
    </Suspense>
  )
}
export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <ErrorPage />,
    children: [{ index: true, element: load(() => import('../routes/public/LandingPage')) }],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: load(() => import('../routes/auth/LoginPage')) },
      { path: '/register', element: load(() => import('../routes/auth/RegisterPage')) },
      {
        path: '/forgot-password',
        element: load(() => import('../routes/auth/ForgotPasswordPage')),
      },
      { path: '/reset-password', element: load(() => import('../routes/auth/ResetPasswordPage')) },
      { path: '/auth/confirm', element: load(() => import('../routes/auth/ConfirmEmailPage')) },
      { path: '/auth/invite', element: load(() => import('../routes/auth/InvitePage')) },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/auth/mfa', element: load(() => import('../routes/auth/MfaChallengePage')) },
      {
        element: <AlumniLayout />,
        children: [
          {
            element: <StatusRoute />,
            children: [
              { path: '/app/status', element: load(() => import('../routes/app/StatusPage')) },
            ],
          },
          {
            element: <ProfileSettingsRoute />,
            children: [
              { path: '/app/profile', element: load(() => import('../routes/app/ProfilePage')) },
              { path: '/app/settings', element: load(() => import('../routes/app/SettingsPage')) },
            ],
          },
          {
            element: <AlumniRoute />,
            children: [
              { path: '/app', element: load(() => import('../routes/app/DashboardPage')) },
              {
                path: '/app/directory',
                element: load(() => import('../routes/app/DirectoryPage')),
              },
              {
                path: '/app/directory/:id',
                element: load(() => import('../routes/app/DirectoryProfilePage')),
              },
              { path: '/app/events', element: load(() => import('../routes/app/EventsPage')) },
              {
                path: '/app/events/:id',
                element: load(() => import('../routes/app/EventDetailsPage')),
              },
              {
                path: '/app/communities',
                element: load(() => import('../routes/app/CommunitiesPage')),
              },
              {
                path: '/app/communities/:slug',
                element: load(() => import('../routes/app/CommunityPage')),
              },
              { path: '/app/messages', element: load(() => import('../routes/app/MessagesPage')) },
              {
                path: '/app/messages/announcements',
                element: load(() => import('../routes/app/AnnouncementThreadPage')),
              },
              {
                path: '/app/messages/:conversationId',
                element: load(() => import('../routes/app/ConversationPage')),
              },
            ],
          },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: '/admin', element: load(() => import('../routes/admin/AdminDashboardPage')) },
              {
                path: '/admin/applications',
                element: load(() => import('../routes/admin/ApplicationsPage')),
              },
              {
                path: '/admin/settings',
                element: load(() => import('../routes/admin/AdminSettingsPage')),
              },
              {
                path: '/admin/events',
                element: load(() => import('../routes/admin/AdminEventsPage')),
              },
              {
                path: '/admin/communities',
                element: load(() => import('../routes/admin/AdminCommunitiesPage')),
              },
              {
                path: '/admin/moderation',
                element: load(() => import('../routes/admin/ModerationPage')),
              },
              {
                element: <GlobalOperationsRoute />,
                children: [
                  {
                    path: '/admin/alumni',
                    element: load(() => import('../routes/admin/AlumniAdminPage')),
                  },
                  {
                    path: '/admin/users',
                    element: load(() => import('../routes/admin/AdminUsersPage')),
                  },
                  {
                    path: '/admin/announcements',
                    element: load(() => import('../routes/admin/AnnouncementsPage')),
                  },
                  {
                    path: '/admin/activity',
                    element: load(() => import('../routes/admin/OperationalActivityPage')),
                  },
                ],
              },
              {
                element: <SuperadminRoute />,
                children: [
                  {
                    path: '/admin/operations-admins',
                    element: load(() => import('../routes/admin/OperationsAdminsPage')),
                  },
                  {
                    path: '/admin/batches',
                    element: load(() => import('../routes/admin/BatchesPage')),
                  },
                  {
                    path: '/admin/audit',
                    element: load(() => import('../routes/admin/AuditPage')),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: load(() => import('../routes/NotFoundPage')) },
])
